import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {Alert, Badge, Button, Card, Form, Table} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {getAdminVideoReport, openAdminVideoReportEvent} from '../api';
import Configuration from '../config';
import {openVideoPlayer, preconnectVideoPlayer} from '../utils/video-player-preload';
import {reportStatusLabels, reportIssueLabels, romeDate, videoReportCsv} from '../util/video-report';
import '../css/admin-video-report.css';

const variants = {complete: 'success', partial: 'warning', failed: 'danger', processing: 'info', waiting: 'info', delayed: 'warning'};

export function AdminVideoReport({token}) {
  const {t} = useTranslation();
  const [date, setDate] = useState(romeDate);
  const [refresh, setRefresh] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [partner, setPartner] = useState('');
  const [status, setStatus] = useState('');
  const [attention, setAttention] = useState(false);
  const [search, setSearch] = useState('');
  const [openingSlot, setOpeningSlot] = useState(null);
  const [playError, setPlayError] = useState(false);
  const requestId = useRef(0);
  const label = value => t(`VIDEO_REPORT_STATUS_${value.toUpperCase()}`, reportStatusLabels[value] || value);
  const issueLabel = code => t(`VIDEO_REPORT_ISSUE_${code}`, reportIssueLabels[code] || code);
  const formatDate = value => value ? new Date(value).toLocaleString('it-IT', {timeZone: 'Europe/Rome'}) : '—';

  useEffect(() => {
    const id = ++requestId.current;
    setReport(null);
    setError(false);
    setLoading(false);
    if (!date || !token) return undefined;
    setLoading(true);
    (async () => {
      try {
        const response = await getAdminVideoReport(token, date);
        if (id !== requestId.current) return;
        if (response.status !== 200) setError(true);
        else setReport(response.json);
      } catch (_) {
        if (id === requestId.current) setError(true);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    })();
    return () => { requestId.current += 1; };
  }, [token, date, refresh]);

  const events = report ? report.events.filter(event => (
    (!partner || String(event.partnerId) === partner) && (!status || event.status === status) &&
    (!attention || event.needsAttention) && (!search.trim() ||
      [event.slotId, event.bookingId, event.partnerName, event.fieldName, event.description,
        ...event.issues.map(issue => issue.code)].join(' ').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))
  )) : [];
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([videoReportCsv(events)], {type: 'text/csv;charset=utf-8;'}));
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-video-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const badge = value => <Badge variant={variants[value] || 'secondary'}>{label(value)}</Badge>;
  const openEvent = async event => {
    if (openingSlot != null) return;
    setPlayError(false);
    setOpeningSlot(event.slotId);
    preconnectVideoPlayer();
    const playerWindow = window.open('', '_blank');
    if (playerWindow) playerWindow.opener = null;
    try {
      const response = await openAdminVideoReportEvent(token, event.slotId);
      const playback = response.json && response.json.playback;
      if (response.status !== 200 || !playback || !playback.krpanoToken || String(playback.slotId) !== String(event.slotId)) {
        throw new Error('video_unavailable');
      }
      const params = new URLSearchParams({slotId: String(playback.slotId), token: playback.krpanoToken});
      const url = `${Configuration.krpanoPlayerUrl}?${params.toString()}`;
      if (playerWindow && !playerWindow.closed) playerWindow.location.href = url;
      else openVideoPlayer(url, 'noopener,noreferrer');
    } catch (_) {
      if (playerWindow && !playerWindow.closed) playerWindow.close();
      setPlayError(true);
    } finally {
      setOpeningSlot(null);
    }
  };

  return <Card className="video-report">
    <Card.Body>
      <h2>{t('VIDEO_REPORT_TITLE', 'Report giornaliero eventi video')}</h2>
      <p>{t('VIDEO_REPORT_INTRO', 'Tutti gli eventi con telecamere richieste e inizio nella giornata scelta, in ora italiana.')}</p>
      <div className="video-report__filters">
        <Form.Group controlId="video-report-date">
          <Form.Label>{t('VIDEO_REPORT_DATE', 'Giornata')}</Form.Label>
          <Form.Control type="date" value={date} onChange={event => setDate(event.target.value)}/>
        </Form.Group>
        <Button onClick={() => setRefresh(value => value + 1)} disabled={loading || !date || !token}>
          {t('VIDEO_REPORT_REFRESH', 'Aggiorna report')}
        </Button>
        <Button variant="outline-primary" onClick={exportCsv} disabled={!report || !events.length || loading}>
          {t('VIDEO_REPORT_EXPORT', 'Esporta righe filtrate (CSV)')}
        </Button>
      </div>
      {loading && <p role="status">{t('VIDEO_REPORT_LOADING', 'Caricamento report…')}</p>}
      {error && <Alert variant="danger" role="alert">{t('VIDEO_REPORT_LOAD_ERROR', 'Impossibile caricare il report. Riprova con Aggiorna report.')}</Alert>}
      {playError && <Alert variant="warning" role="alert">{t('VIDEO_REPORT_PLAY_ERROR', 'Impossibile aprire il video. Potrebbe non essere più disponibile: aggiorna il report e riprova.')}</Alert>}
      {report && <>
        <p><small>{t('VIDEO_REPORT_UPDATED', 'Ultimo aggiornamento')}: {formatDate(report.generatedAt)} · Europe/Rome</small></p>
        <div className="video-report__summary" aria-label={t('VIDEO_REPORT_SUMMARY', 'Riepilogo di tutta la giornata')}>
          {[
            [t('VIDEO_REPORT_TOTAL', 'Eventi totali'), report.summary.total],
            [label('not_requested'), report.summary.byStatus.not_requested || 0],
            [label('complete'), report.summary.byStatus.complete],
            [label('partial'), report.summary.byStatus.partial],
            [label('failed'), report.summary.byStatus.failed],
            [label('delayed'), report.summary.byStatus.delayed || 0],
            [t('VIDEO_REPORT_ACTIVE', 'In corso / in attesa'), report.summary.byStatus.processing + report.summary.byStatus.waiting],
            [t('VIDEO_REPORT_ATTENTION', 'Da controllare'), report.summary.needsAttention]
          ].map(([title, value]) => <div key={title}><span>{title}</span><strong>{value}</strong></div>)}
        </div>
        {report.summary.allSuccessful && <Alert variant="success">{t('VIDEO_REPORT_ALL_SUCCESS', 'Tutti gli eventi della giornata risultano completati integralmente.')}</Alert>}
        <p className="video-report__note">{t('VIDEO_REPORT_EVIDENCE', 'Completo richiede esito positivo, pubblicazione finale della stessa esecuzione e al massimo 10 s mancanti per video, senza regia segnalata assente. La verifica usa le evidenze registrate nel sistema. I video storici privi di queste evidenze restano da verificare.')}</p>
        {report.errors.length > 0 && <details className="video-report__diagnostics">
          <summary>{t('VIDEO_REPORT_ERRORS', 'Criticità e segnalazioni di tutta la giornata')}</summary>
          <ul>{report.errors.map(item => <li key={item.code}>{issueLabel(item.code)} — <code>{item.code}</code>: {item.eventCount} {t('VIDEO_REPORT_EVENTS', 'eventi')}</li>)}</ul>
        </details>}
        <div className="video-report__filters">
          <Form.Group controlId="video-report-partner">
            <Form.Label>{t('VIDEO_REPORT_PARTNER', 'Centro')}</Form.Label>
            <Form.Control as="select" value={partner} onChange={event => setPartner(event.target.value)}>
              <option value="">{t('VIDEO_REPORT_ALL_CENTERS', 'Tutti i centri')}</option>
              {report.partners.map(item => <option key={item.partnerId} value={item.partnerId}>{item.partnerName} ({item.needsAttention}/{item.total} {t('VIDEO_REPORT_TO_CHECK', 'da controllare')})</option>)}
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="video-report-status">
            <Form.Label>{t('VIDEO_REPORT_OUTCOME', 'Esito')}</Form.Label>
            <Form.Control as="select" value={status} onChange={event => setStatus(event.target.value)}>
              <option value="">{t('VIDEO_REPORT_ALL_STATUSES', 'Tutti gli esiti')}</option>
              {Object.keys(reportStatusLabels).map(value => <option key={value} value={value}>{label(value)} ({report.summary.byStatus[value]})</option>)}
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="video-report-search">
            <Form.Label>{t('VIDEO_REPORT_SEARCH', 'Cerca evento, campo o codice errore')}</Form.Label>
            <Form.Control value={search} onChange={event => setSearch(event.target.value)}/>
          </Form.Group>
          <Form.Check id="video-report-attention" label={t('VIDEO_REPORT_ONLY_ATTENTION', 'Solo da controllare')} checked={attention} onChange={event => setAttention(event.target.checked)}/>
        </div>
        <p role="status">{events.length} / {report.summary.total} {t('VIDEO_REPORT_DISPLAYED', 'eventi visualizzati')}</p>
        <Table responsive hover className="video-report__table">
          <thead><tr><th>{t('VIDEO_REPORT_EVENT', 'Evento / orario')}</th><th>{t('VIDEO_REPORT_LOCATION', 'Centro / campo')}</th><th>{t('VIDEO_REPORT_OUTCOME', 'Esito')}</th><th>{t('VIDEO_REPORT_DETAILS', 'Diagnosi e dettaglio')}</th></tr></thead>
          <tbody>{events.map(event => <tr key={event.slotId}>
            <td><strong>#{event.slotId}</strong>{event.bookingId && <small> · {t('VIDEO_REPORT_BOOKING', 'Prenotazione')} #{event.bookingId}</small>}<br/>{event.description}<br/><small>{formatDate(event.startDatetime)} → {formatDate(event.endDatetime)}</small></td>
            <td>{event.partnerName}<br/>{event.fieldName}</td>
            <td>{badge(event.status)}{event.liveStartDelayed && event.status !== 'delayed' && <div>{badge('delayed')}</div>}{event.needsAttention && <div>{t('VIDEO_REPORT_TO_CHECK', 'Da controllare')}</div>}</td>
            <td>
              {event.playbackAvailable && <div><Button size="sm" onClick={() => openEvent(event)} disabled={openingSlot != null}>
                {t(event.playbackIsLive ? 'VIDEO_REPORT_OPEN_LIVE' : 'VIDEO_REPORT_OPEN_VIDEO', event.playbackIsLive ? 'Apri diretta' : 'Apri video')}
              </Button></div>}
              {event.status === 'not_requested' && <div>{t('VIDEO_REPORT_NOT_REQUESTED_NOTE', 'Trasmissione non richiesta: nessun acquisto o avvio automatico previsto. Non costituisce un errore.')}</div>}
              {Array.from(new Set(event.issues.map(item => item.code))).map(code => <div key={code}>{issueLabel(code)} <code>{code}</code></div>)}
              <details><summary>{t('VIDEO_REPORT_CAMERAS', 'Telecamere e tentativi')} ({event.cameras.length})</summary>
                <ul>{event.cameras.map(camera => <li key={camera.cameraId}>
                  <strong>{camera.name} #{camera.cameraId}</strong>: {badge(camera.status)}<br/>
                  {t('VIDEO_REPORT_EXPECTED_DURATION', 'Durata prevista')}: {event.expectedDurationSeconds} s · {t('VIDEO_REPORT_AVAILABLE_DURATION', 'Disponibile')}: {camera.durationSeconds} s
                  {camera.missingSeconds != null && <> · {t('VIDEO_REPORT_MISSING_SECONDS', 'Mancanti')}: {camera.missingSeconds} s</>}
                  {camera.issues.map(item => <div key={item.code}>{issueLabel(item.code)} <code>{item.code}</code></div>)}
                  {camera.commandId && <small>{camera.commandStatus} · {camera.commandId}</small>}
                </li>)}</ul>
                <p>{t('VIDEO_REPORT_ATTEMPTS_NOTE', 'Storico comandi, dal più recente. Gli errori dei tentativi precedenti non determinano l’esito attuale.')}</p>
                <ul>{event.commands.map(command => <li key={command.id}><code>{command.id}</code> · {command.status} · {t('VIDEO_REPORT_ATTEMPTS', 'Tentativi')}: {command.attemptCount} · {formatDate(command.updatedAt)} {command.errorCode && <code>{command.errorCode}</code>}{command.nextAttemptAt && <> · {t('VIDEO_REPORT_NEXT_RETRY', 'Prossimo tentativo')}: {formatDate(command.nextAttemptAt)}</>}</li>)}</ul>
              </details>
            </td>
          </tr>)}{events.length === 0 && <tr><td colSpan={4}>{t('VIDEO_REPORT_EMPTY', 'Nessun evento corrisponde alla giornata e ai filtri selezionati.')}</td></tr>}</tbody>
        </Table>
      </>}
    </Card.Body>
  </Card>;
}

export default connect(state => ({token: state.login && state.login.token}))(AdminVideoReport);
